import Close16 from '@carbon/icons-react/es/close/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { Tooltip } from 'antd';
import { ButtonType } from 'antd/lib/button';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { InputProps } from 'antd/lib/input';
import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop, switchProp } from 'styled-tools';
import { Button } from 'components/atoms/button';
import { queueMacrotask } from 'utils/macrotask';
import { IconWrapper } from '../icon-shell/icon-shell';
import { Height, HeightStyle, InputFocus, InputStyle, Input } from './input';

type ExpandDirection = 'left' | 'right';
const SearchButton = styled(Button).attrs({
  icon: (
    <IconWrapper>
      <Search16 />
    </IconWrapper>
  ),
})``;
const CloseButton = styled(Button).attrs({ icon: <Close16 /> })``;
const Wrapper = styled.div<{ active: boolean; size: SizeType }>`
  display: flex;
  align-items: center;
  transition: all 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
  max-width: ${HeightStyle};
  overflow: hidden;

  ${ifProp(
    'active',
    css`
      ${InputStyle};
      max-width: 100%;
      padding: 0;
      &:focus-within {
        ${InputFocus};
      }
      ${SearchButton} {
        pointer-events: none;
      }
    `,
  )};

  ${Input} {
    padding: 0;
    height: calc(${HeightStyle} - 6px);
    border: none;
    outline: none;
  }
`;
const AnimationWrapper = styled.div<{
  active: boolean;
  size: SizeType;
  direction: ExpandDirection;
  minWidth?: string;
}>`
  position: relative;
  min-width: ${(props) => props.minWidth ?? '180px'};
  width: 100%;
  ${Height};
  ${Wrapper} {
    position: absolute;
    ${switchProp(prop('direction', 'left'), {
      left: css`
        left: 0px;
      `,
      right: css`
        right: 0px;
      `,
    })};
  }
`;

type SearchInputProps = InputProps & {
  direction?: ExpandDirection;
  tooltip?: ReactNode;
  searchButtonProps?: {
    type: ButtonType;
  };
  minWidth?: string;
};

export const SearchInput: FC<SearchInputProps> = ({
  onChange,
  onBlur,
  onFocus,
  size = 'middle',
  direction = 'left',
  className,
  tooltip,
  searchButtonProps = { type: 'default' },
  minWidth,
  ...props
}) => {
  const inputRef = useRef<any>(null);
  const [active, setActive] = useState(false);
  const [value, setValue] = useState('');

  const openSearchInput = useCallback(() => {
    setActive(true);
    queueMacrotask(() => {
      inputRef.current?.focus();
    });
  }, []);

  const onChangeValue = (e) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  return (
    <AnimationWrapper
      active={active}
      size={size}
      direction={direction}
      className={className}
      minWidth={minWidth}
    >
      <Wrapper active={active} size={size} className="search-input-wrapper">
        <Tooltip title={tooltip}>
          <SearchButton
            size={size}
            type={active ? 'text' : searchButtonProps.type}
            tabIndex={active ? -1 : 0}
            onFocus={openSearchInput}
            onClick={openSearchInput}
            data-cy="search-input.button"
          />
        </Tooltip>
        <Input
          {...props}
          tabIndex={active ? 0 : -1}
          ref={inputRef}
          value={value}
          onChange={onChangeValue}
          onFocus={(e) => {
            setActive(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            if (!e.target.value) {
              setActive(false);
            }
            onBlur?.(e);
          }}
          data-cy="search.text-field"
        />
        <CloseButton
          data-cy="search-input.close-button"
          type="text"
          size={size}
          tabIndex={-1}
          onClick={(e) => {
            setActive(false);
            const event = Object.create(e);
            const target = inputRef.current.input;
            const currentTarget = target.cloneNode(true) as HTMLInputElement;

            event.target = currentTarget;
            event.currentTarget = currentTarget;
            currentTarget.value = '';

            onChangeValue(event as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      </Wrapper>
    </AnimationWrapper>
  );
};
